import { useEffect, useState } from "react"

import { useQuery } from "@tanstack/react-query"

import type { AvailableLookupConsumer } from "@common-shared/config/lookup.config.ts"
import { PolicyModality } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"

import type {
    ISelectRawOption,
    ISelectRenderedOption,
} from "@front-shared/types/objects.types.ts"

import { UtilHelper } from "../helpers/util.helper"
import { LookupService } from "../services/lookup.service"

// refetch once in 1h by default
export const useCptCodes = (
    consumer?: AvailableLookupConsumer,
    neededModalities = [PolicyModality.MENTAL_HEALTH],
    staleTime = 60 * 60 * 1000,
) => {
    const defaultData = {
        availableCptCodesByModalities: {},
        availableCptCodes: [],
        availableCptCodesStyled: [],
    }

    const [data, setData] = useState<IUseCptCodesData>(defaultData)

    const query = useQuery({
        staleTime,
        queryKey: [
            `useCptCodes-${consumer || "all"}-${neededModalities.toString()}`,
        ],
        queryFn: async () => {
            return await LookupService.lookupCptCodes(
                consumer,
                neededModalities,
            )
        },
    })

    useEffect(() => {
        if (!query.data) {
            setData(defaultData)

            return
        }

        // This reducer creates a response with the initial query.data in the form of
        // Partial<Record<PolicyModality, ISelectRenderedOption[]>> as availableCptCodesByModalities
        // and then aggregated arrays (all requested modalities together) of either
        // ISelectRenderedOption or ISelectRawOption to be used as needed.
        const useCptCodesData = Object.values(query.data)
            .flat()
            // Sort by CPT code in Unicode collation order based on the defaults for the locale (numbers first, then letters)
            .sort((a, b) => a.value.localeCompare(b.value))
            .reduce(
                (acc: IUseCptCodesData, item) => {
                    const label = (
                        <div className="flex items-center w-full">
                            <div className="flex-1 overflow-hidden text-ellipsis">
                                {item.displayValue}
                            </div>

                            <div className="dropdown-value-additional text-brand-warmShadow ml-[16px]">
                                {item.value}
                            </div>
                        </div>
                    )

                    acc.availableCptCodesStyled.push({
                        key: `${
                            item.value
                        }-${UtilHelper.getRandomInt()}-${UtilHelper.getRandomInt()}`,
                        value: item.value,
                        label,
                    })

                    acc.availableCptCodes.push({
                        ...item,
                        dropdownDisplayValue: label,
                    })

                    return acc
                },
                {
                    ...defaultData,

                    availableCptCodesByModalities: query.data,
                },
            )

        setData(useCptCodesData)
    }, [query.data])

    return {
        isLoading: query.isLoading,

        ...data,
    }
}

interface IUseCptCodesData {
    availableCptCodesByModalities: Partial<
        Record<PolicyModality, ISelectRenderedOption[]>
    >
    availableCptCodes: ISelectRenderedOption[]
    availableCptCodesStyled: ISelectRawOption[]
}
